<template>
    <div>
        <v-layout column justify-center align-center>
            <h1>Dev Landing Page</h1>
            <p>In the future, we should probably have '/' redirect to the sponsor search flow.</p>
            <h2>Setup Environment</h2>
            <div style="display: flex; flex-direction: row">
                <ns-select v-model="selectedMarket" label="Market" :items="markets" />
                <ns-select v-model="selectedLanguage" label="Language" :items="selectedMarketInfo.languages" />
                <ns-select v-model="selectedSignupType" label="Signup Type" :items="selectedMarketInfo.signupTypes" />
            </div>
            <br />
            <button class="primary-outline" @click="setLocale">Set Locale in RunConfig (for Checkout)</button>
            <br />
            <label>
                The form is in Conversion Mode
                <input v-model="isConversion" type="checkbox">
            </label>
            <label>
                Sponsor ID
                <input
                    v-model="sponsorId"
                    type="text"
                    pattern="^[A-Z]{2}\d{7,8}$"
                    list="sponsorIdSuggestions"
                />
                <datalist id="sponsorIdSuggestions">
                    <option value="US9470258">Test Emp - US9470258</option>
                    <option value="UK1111111">Bruce Wayne - UK1111111</option>
                </datalist>
            </label>
            <br />
            <label>
                Invite ID
                <input
                    v-model="inviteId"
                    type="text"
                />
            </label>
            <br />
            <h2>Flows</h2>
            <ul>
                <li>
                    <nuxt-link :to="sponsorSearchRoute">
                        Sponsor Search (nuskin.com flow)
                    </nuxt-link>
                </li>
                <li>
                    <nuxt-link :to="inviteLandingRoute">
                        Invite Landing
                    </nuxt-link>
                </li>
                <li>
                    <nuxt-link :to="signupPageRoute">
                        Signup Page
                    </nuxt-link>
                </li>
                <li>
                    <nuxt-link :to="checkoutRedirectRoute">
                        Checkout-Redirect (Vera/MySite Flow)
                    </nuxt-link>
                </li>
                <li>
                    <nuxt-link :to="checkoutRoute">
                        Checkout Signup Page (Vera/MySite Flow)
                    </nuxt-link>
                </li>
                <li>
                    <nuxt-link :to="guestSignupPageRoute">
                        Guest Signup
                    </nuxt-link>
                </li>
            </ul>
            <h2 style="margin-top: 2em;"><nuxt-link to="devtools/">Developer Tools</nuxt-link></h2>
        </v-layout>

    </div>
</template>

<script>
/* istanbul ignore file */
import {RunConfigService, SponsorStorageService} from '@nuskin/ns-util';
import * as routes from '~/utils/routing.js';
import {Origins} from '~/utils/constants.js';
import {VLayout} from 'vuetify/lib';
import {NsSelect} from '@nuskin/design-components';
import {signupMarkets} from '@nuskin/account-components';
import apiClient from '../utils/apiClient.js';

const signupTypeNames = new Map([
    ['BRAND_AFFILIATE', 'Brand Affiliate/Distributor'],
    ['MEMBER', 'Member/Preferred Customer'],
    ['CUSTOMER', 'Retail Customer']
]);

export default {
    components: {
        VLayout, NsSelect
    },
    data() {
        const runConfig = RunConfigService.getRunConfig();
        return {
            markets: [...signupMarkets.asMarketMap.keys()].sort(),
            inviteId: 'CustomInviteId',
            selectedMarket: runConfig.country || 'US',
            selectedLanguage: runConfig.language || 'en',
            selectedSignupType: 'BRAND_AFFILIATE',
            sponsorId: 'US9470258',
            routes: routes,
            isConversion: false
        };
    },
    computed: {
        selectedLocale() {
            return `${this.selectedLanguage}_${this.selectedMarket}`;
        },
        selectedMarketInfo() {
            return signupMarkets.asMarketMap.get(this.selectedMarket);
        },
        signupTypes() {
            return this.selectedMarketInfo.signupTypes.map((value) => ({
                value,
                text: signupTypeNames.get(value)
            }));
        },
        sponsorSearchRoute() {
            return routes.sponsorSearch({
                locale: this.selectedLocale,
                signupType: this.selectedSignupType
            });
        },
        inviteLandingRoute() {
            return routes.inviteLanding({
                locale: this.selectedLocale,
                signupType: this.selectedSignupType,
                inviteId: this.inviteId,
                sponsor: this.sponsorId
            });
        },
        signupPageRoute() {
            return routes.signupPage({
                locale: this.selectedLocale,
                signupType: this.selectedSignupType,
                inviteId: this.inviteId,
                sponsor: this.sponsorId,
                origin: Origins.NU_SKIN_DOT_COM,
                conversion: this.isConversion
            });
        },
        guestSignupPageRoute() {
            return routes.guestSignupPage({
                locale: this.selectedLocale,
                inviteId: this.inviteId,
                sponsor: this.sponsorId,
                origin: Origins.NU_SKIN_DOT_COM
            });
        },
        checkoutRoute() {
            return routes.checkout({
                locale: this.selectedLocale,
                signupType: this.selectedSignupType,
                sponsor: this.sponsorId
            });
        },
        checkoutRedirectRoute() {
            return routes.checkoutRedirect({
                signupType: this.selectedSignupType
            });
        }
    },
    watch: {
        sponsorId: {
            immediate: true,
            handler(value) {
                SponsorStorageService.setSponsor(value || null);
            }
        },
        selectedMarketInfo: {
            immediate: true,
            handler({languages, signupTypes}) {
                if (!signupTypes.includes(this.selectedSignupType)) {
                    this.selectedSignupType = signupTypes[0];
                }
                if (!languages.includes(this.selectedLanguage)) {
                    this.selectedLanguage = languages[0];
                }
            }
        }
    },
    mounted() {
        // Clear out any stored signup session
        apiClient.currentSessionId = null;
    },
    methods: {
        setLocale() {
            const [language, country] = this.selectedLocale.split('_');
            RunConfigService.addRunConfig({country, language});
        }
    }
};
</script>
<style scoped lang="scss">
@use "~@nuskin/ns-core-styles/src/scss/_vuetifyOverrides.scss";
@use "~@nuskin/ns-core-styles/src/scss/buttons.scss";
@use "~@nuskin/ns-core-styles/src/scss/colors.scss";
select {
    border-style: solid;
    border-width: 1px;
    -moz-appearance: auto !important;
    -webkit-appearance: auto !important;
    appearance: auto !important;
}
</style>
